// Die rechte Spalte beim Abwaegen: die Auswertung der KI (seit 23.09.2026).
//
// Aufgebaut wie "Aus dem Gespraech" im Vertragsstudio - Kopf, Reiter mit
// orangem Strich, darunter der Inhalt. Drei Reiter, weil der Bearbeiter drei
// Dinge tut:
//   Offen       - worueber er noch mit der KI spricht: uebernehmen oder
//                 kommentieren; die Kommentare gehen gesammelt an die KI.
//   Uebernommen - was abgestimmt ist. Die KI fasst es nicht mehr an.
//   Antwort     - der Text an den Kunden, aus dem Uebernommenen verfasst:
//                 bearbeiten, kopieren, als Kommentar an den Deal.
//
// Gefuehrt statt erklaert (Ueberarbeitung 23.09.2026): Die Seite wechselt
// selbst dorthin, wo es weitergeht - nach einer Runde der KI zu "Offen", nach
// dem letzten "Passt" zu "Antwort". Erklaertexte stehen nur noch dort, wo
// noch nichts da ist; die Leiste fuer Kommentare erscheint erst, wenn einer
// wartet.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, ClipboardCopy, Scale, Send, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { T, knopfStil, primaerKnopfStil, feldStil, karteStil, reiterStil } from '../../lib/designTokens';
import { OffenerPunkt, UebernommenerPunkt } from './AbwaegenPunktKarte';
import { Wegweiser } from './abwaegenAnzeige';

const ruhigerText = { fontSize: T.tText, color: T.muted, lineHeight: 1.6 };

const textKnopfStil = {
  display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
  padding: 0, fontSize: T.tMeta, fontWeight: 700, color: T.navy, cursor: 'pointer',
};

async function inZwischenablage(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    // Ohne Clipboard-API (etwa ohne HTTPS) der alte Weg ueber ein Hilfsfeld.
    const feld = document.createElement('textarea');
    feld.value = text;
    feld.setAttribute('readonly', '');
    feld.style.position = 'fixed';
    feld.style.opacity = '0';
    document.body.appendChild(feld);
    feld.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(feld);
    return ok;
  }
}

function belegZu(belege, punkt) {
  return (belege || []).find((b) => (b?.id ? b.id === punkt.id : String(b?.nummer ?? '') === String(punkt?.nummer ?? '')));
}

const SCHRITTE = [
  ['Wünsche eingeben', 'links als Text, Mail oder Datei'],
  ['Punkte abstimmen', 'übernehmen, was passt, den Rest kommentieren'],
  ['Antwort verschicken', 'kopieren oder als Kommentar an den Deal'],
];

/** Waehrend der ersten Auswertung: Platzhalter statt leerer Flaeche. */
function Platzhalter() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div role="status" style={{ ...ruhigerText, marginBottom: 4 }}>Die KI zerlegt die Wünsche in Punkte und prüft jeden am Vertrag …</div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="abwaegen-laedt" style={{ ...karteStil, padding: 16, animationDelay: `${i * 160}ms` }}>
          <div style={{ height: 12, width: '45%', background: T.randWeich, borderRadius: 6 }} />
          <div style={{ height: 9, width: '92%', background: T.hairline, borderRadius: 6, marginTop: 14 }} />
          <div style={{ height: 9, width: '70%', background: T.hairline, borderRadius: 6, marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

function AntwortReiter({
  uebernommen, offenZahl, fazit, fazitAus, bitrixGesendetAm, dealId, laeuft, kiBereit,
  onVerfassen, onSpeichern, onNachBitrix, onZuOffen,
}) {
  const [entwurf, setEntwurf] = useState(fazit || '');
  // Kommt eine neue Fassung vom Server (verfasst oder nach dem Laden), ersetzt
  // sie das Feld. Getippte Aenderungen sind beim Verlassen schon gespeichert.
  useEffect(() => { setEntwurf(fazit || ''); }, [fazit]);

  const veraltet = useMemo(() => {
    if (!fazit || !Array.isArray(fazitAus) || fazitAus.length === 0) return false;
    const jetzt = uebernommen.map((p) => p.id).sort().join('|');
    return jetzt !== [...fazitAus].sort().join('|');
  }, [fazit, fazitAus, uebernommen]);

  const nochOffen = offenZahl > 0 ? (
    <button type="button" onClick={onZuOffen} style={textKnopfStil}>
      {offenZahl === 1 ? '1 Punkt noch offen' : `${offenZahl} Punkte noch offen`} <ArrowRight size={12} />
    </button>
  ) : null;

  if (!uebernommen.length && !fazit) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
        <div style={ruhigerText}>Noch nichts abgestimmt. Aus den übernommenen Punkten schreibt die KI hier den Brief an den Kunden.</div>
        {nochOffen}
      </div>
    );
  }

  const beschaeftigt = Boolean(laeuft);
  const text = entwurf.trim();
  const verfassenGesperrt = beschaeftigt || !kiBereit || !uebernommen.length;
  const verfassenKnopf = (stil) => (
    <button type="button" onClick={onVerfassen} disabled={verfassenGesperrt}
      className={laeuft === 'antwort' ? 'abwaegen-laedt' : undefined}
      style={{ ...stil, opacity: verfassenGesperrt && laeuft !== 'antwort' ? 0.45 : 1 }}>
      <Sparkles size={12} />
      {laeuft === 'antwort' ? 'Die KI schreibt …' : (fazit ? 'Neu verfassen' : 'Antwort verfassen')}
    </button>
  );

  // Vor dem ersten Verfassen: ein klarer naechster Schritt statt eines
  // einzelnen Knopfs auf leerer Flaeche.
  if (!fazit && !text) {
    return (
      <div style={{ maxWidth: 420, margin: '36px auto 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
          {offenZahl ? 'Antwort vorbereiten' : 'Alles abgestimmt'}
        </div>
        <div style={ruhigerText}>
          Die KI schreibt aus {uebernommen.length === 1 ? 'dem übernommenen Punkt' : `den ${uebernommen.length} übernommenen Punkten`} einen
          Brief an den Kunden. Du kannst ihn danach frei bearbeiten.
        </div>
        <div style={{ marginTop: 6 }}>{verfassenKnopf(primaerKnopfStil)}</div>
        {nochOffen ? <div style={{ marginTop: 4 }}>{nochOffen}</div> : null}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {verfassenKnopf(veraltet ? primaerKnopfStil : knopfStil())}
        {veraltet ? <span style={{ fontSize: T.tMeta, color: T.warn }}>Die Auswahl hat sich seitdem geändert.</span> : null}
        <span style={{ flex: 1 }} />
        {nochOffen}
      </div>

      <textarea value={entwurf} onChange={(e) => setEntwurf(e.target.value)} rows={16} disabled={beschaeftigt}
        aria-label="Antwort an den Kunden"
        onBlur={() => { if ((fazit || '') !== entwurf) onSpeichern(entwurf); }}
        style={{ ...feldStil, resize: 'vertical', lineHeight: 1.7, padding: '14px 16px' }} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={!text}
          onClick={async () => {
            const ok = await inZwischenablage(entwurf);
            if (ok) toast.success('Antwort kopiert.');
            else toast.error('Kopieren ging nicht — bitte markieren und selbst kopieren.');
          }}
          style={{ ...knopfStil(true), opacity: text ? 1 : 0.45 }}>
          <ClipboardCopy size={12} /> Kopieren
        </button>
        <button type="button" onClick={() => onNachBitrix(entwurf)} disabled={!dealId || beschaeftigt || !text}
          title={dealId ? '' : 'Dafür oben einen Deal laden'}
          style={{ ...knopfStil(), opacity: (!dealId || beschaeftigt || !text) ? 0.45 : 1 }}>
          <Briefcase size={12} />
          {laeuft === 'bitrix' ? 'Wird gesendet …' : 'Als Kommentar an den Deal'}
        </button>
        {bitrixGesendetAm ? (
          <span style={{ fontSize: T.tMikro, color: T.gut }}>
            am Deal seit {new Date(bitrixGesendetAm).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function AbwaegenAuswertung({
  abwaegung, belege, belegeLaden, laeuft, kiBereit, dealId, runde, entwuerfe, kommentarZahl,
  onEntwurf, onStatus, onKommentar, onKommentareSenden, onVerfassen, onFazitSpeichern, onNachBitrix,
}) {
  const [reiter, setReiter] = useState('offen');
  const inhalt = useRef(null);
  const bewertung = abwaegung?.bewertung;
  const punkte = useMemo(() => bewertung?.punkte || [], [bewertung]);
  const offen = punkte.filter((p) => p.status !== 'uebernommen');
  const uebernommen = punkte.filter((p) => p.status === 'uebernommen');
  const kiArbeitet = laeuft === 'abwaegen';
  const gesperrt = Boolean(laeuft);

  // Nach jeder Runde der KI: zu "Offen", oben anfangen - aber nur, wenn dort
  // etwas liegt. Beantwortet die KI bloss eine Frage, bleibt man, wo man war
  // (etwa beim Brief im Reiter "Antwort").
  const offenZahl = useRef(offen.length);
  offenZahl.current = offen.length;
  useEffect(() => {
    if (offenZahl.current > 0) setReiter('offen');
    if (inhalt.current) inhalt.current.scrollTop = 0;
  }, [runde]);

  // Nach dem letzten "Passt" geht es mit der Antwort weiter.
  const offenVorher = useRef(offen.length);
  useEffect(() => {
    if (offenVorher.current > 0 && offen.length === 0 && uebernommen.length > 0) setReiter('antwort');
    offenVorher.current = offen.length;
  }, [offen.length, uebernommen.length]);

  let inhaltOffen;
  if (!bewertung) inhaltOffen = kiArbeitet ? <Platzhalter /> : <Wegweiser schritte={SCHRITTE} />;
  else if (offen.length === 0) {
    inhaltOffen = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
        <div style={ruhigerText}>Alles abgestimmt. Neue Wünsche kannst du links jederzeit nachreichen.</div>
        <button type="button" onClick={() => setReiter('antwort')} style={textKnopfStil}>
          Zur Antwort <ArrowRight size={12} />
        </button>
      </div>
    );
  } else {
    inhaltOffen = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {offen.map((punkt) => (
          <OffenerPunkt key={`${punkt.id}-${runde}`} punkt={punkt} mitVertrag={bewertung.mitVertrag}
            beleg={belegZu(belege, punkt)} belegeLaden={belegeLaden} gesperrt={gesperrt}
            entwurf={entwuerfe[punkt.id]} onEntwurf={onEntwurf}
            onUebernehmen={(id) => onStatus(id, 'uebernommen')} onKommentar={onKommentar} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{
        padding: '11px 16px', borderBottom: `1px solid ${T.randWeich}`, background: T.flaeche,
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <Scale size={14} color={T.navy} />
        <span style={{ flex: 1, minWidth: 0, fontSize: T.tTitel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
          Auswertung
        </span>
        {kiArbeitet && bewertung ? (
          <span role="status" className="abwaegen-laedt" style={{ fontSize: T.tMikro, color: T.orangeTinte, fontWeight: 700 }}>Die KI überarbeitet …</span>
        ) : null}
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${T.randWeich}`, background: T.grund, flexShrink: 0 }}>
        <button type="button" onClick={() => setReiter('offen')} style={reiterStil(reiter === 'offen')}>
          Offen{punkte.length ? ` (${offen.length})` : ''}
        </button>
        <button type="button" onClick={() => setReiter('uebernommen')} style={reiterStil(reiter === 'uebernommen')}>
          Übernommen{punkte.length ? ` (${uebernommen.length})` : ''}
        </button>
        <button type="button" onClick={() => setReiter('antwort')} style={reiterStil(reiter === 'antwort')}>
          Antwort
        </button>
      </div>

      <div ref={inhalt} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {reiter === 'offen' ? inhaltOffen : null}

          {reiter === 'uebernommen' ? (
            uebernommen.length === 0 ? (
              <div style={ruhigerText}>Noch nichts übernommen.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {uebernommen.map((punkt) => (
                  <UebernommenerPunkt key={punkt.id} punkt={punkt} gesperrt={gesperrt}
                    onZurueck={(id) => onStatus(id, 'offen')} />
                ))}
              </div>
            )
          ) : null}

          {reiter === 'antwort' ? (
            <AntwortReiter uebernommen={uebernommen} offenZahl={offen.length} fazit={abwaegung?.fazit}
              fazitAus={abwaegung?.fazitAus} bitrixGesendetAm={abwaegung?.bitrixGesendetAm} dealId={dealId}
              laeuft={laeuft} kiBereit={kiBereit} onVerfassen={onVerfassen} onSpeichern={onFazitSpeichern}
              onNachBitrix={onNachBitrix} onZuOffen={() => setReiter('offen')} />
          ) : null}
        </div>
      </div>

      {/* Erscheint erst, wenn ein Kommentar wartet - dann ist es der naechste Schritt. */}
      {reiter === 'offen' && kommentarZahl > 0 ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '10px 16px', background: T.flaeche,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
        }}>
          <button type="button" onClick={onKommentareSenden} disabled={gesperrt || !kiBereit}
            style={{ ...primaerKnopfStil, opacity: (gesperrt || !kiBereit) ? 0.45 : 1 }}>
            <Send size={12} />
            {kommentarZahl === 1 ? 'Kommentar an die KI senden' : `${kommentarZahl} Kommentare an die KI senden`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
